import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { DIMENSIONALITY_VALUES } from 'constants/businessTool';
import { t } from 'i18next';

import {
  FORM_TABLE_COLUMN_NAME,
  FORM_TABLE_FIRST_COLUMN_NAME,
} from 'components/_basic/Form/FormTableComponent/constants';

import { getAdditionalColumnProperties } from '../../helpers';

export const FIRST_COLUMN_WIDTH = 342;
export const FIRST_COLUMN_MAXLENGTH = 150;
export const MAX_FIELD_LENGTH = 2;

export const R_AND_D_FORM_NAMES = {
  R_AND_D_CURRENCY: 'rAndDCurrency',
  R_AND_D_TABLE: 'rAndDTable',
  R_AND_D_TABLE_TOTAL: 'totalRAndDTable',
} as const;

export const useGetItemsColumnDefaultValues = () => {
  const { t, i18n } = useTranslation();

  return useMemo(
    () => [
      'Salaries for researchers and developers',
      'Benefits and payroll taxes for researchers and developers',
      t('pdfPages.mappings.financialPlan.randDForm.laboratorySuppliesAndMaterials'),
      t('pdfPages.mappings.financialPlan.randDForm.prototypeDevelopmentAndTesting'),
      t('pdfPages.mappings.financialPlan.randDForm.patentAndLicensingFees'),
      t('pdfPages.mappings.financialPlan.randDForm.researchRelatedSoftwareAndTechnologyPurchases'),
      t('pdfPages.mappings.financialPlan.randDForm.other'),
    ],
    [t, i18n],
  );
};

export const itemsColumnDefaultValues = [
  'Salaries for researchers and developers',
  'Benefits and payroll taxes for researchers and developers',
  t('pdfPages.mappings.financialPlan.randDForm.laboratorySuppliesAndMaterials'),
  t('pdfPages.mappings.financialPlan.randDForm.prototypeDevelopmentAndTesting'),
  t('pdfPages.mappings.financialPlan.randDForm.patentAndLicensingFees'),
  t('pdfPages.mappings.financialPlan.randDForm.researchRelatedSoftwareAndTechnologyPurchases'),
  t('pdfPages.mappings.financialPlan.randDForm.other'),
];

export const rAndDColumns = [
  {
    title: 'financialPlan.plannedExpenses.rAndD.columnTitles.plannedMonthlyExpenses',
    id: 'financialPlan.plannedExpenses.rAndD.columnTitles.plannedMonthlyExpenses',
  },
  {
    title: 'financialPlan.plannedExpenses.rAndD.columnTitles.annualExpenseGrowthRate',
    id: 'financialPlan.plannedExpenses.rAndD.columnTitles.annualExpenseGrowthRate',
  },
];

const additionalProperties: Record<string, string> = getAdditionalColumnProperties(
  FORM_TABLE_COLUMN_NAME,
  MAX_FIELD_LENGTH,
);

export const defaultRAndDItem = { [FORM_TABLE_FIRST_COLUMN_NAME]: '', ...additionalProperties };

export const getDefaultRAndDRows = (defaultValues: string[]) =>
  defaultValues.map((item) => {
    return { [FORM_TABLE_FIRST_COLUMN_NAME]: item, ...additionalProperties };
  });

export type IRow = Record<string, string | number | undefined>;

export interface IRAndDValuesType {
  [R_AND_D_FORM_NAMES.R_AND_D_CURRENCY]: string;
  [R_AND_D_FORM_NAMES.R_AND_D_TABLE]: IRow[];
  [R_AND_D_FORM_NAMES.R_AND_D_TABLE_TOTAL]: IRow;
}

export const useGetDefaultRAndDRows = () => {
  const defaultValues = useGetItemsColumnDefaultValues();

  return useMemo(() => getDefaultRAndDRows(defaultValues), [defaultValues]);
};

export const useGetRAndDInitialValues = (): IRAndDValuesType => {
  const defaultRows = useGetDefaultRAndDRows();

  return useMemo(
    () => ({
      [R_AND_D_FORM_NAMES.R_AND_D_CURRENCY]: DIMENSIONALITY_VALUES.THOUSAND,
      [R_AND_D_FORM_NAMES.R_AND_D_TABLE]: defaultRows,
      [R_AND_D_FORM_NAMES.R_AND_D_TABLE_TOTAL]: defaultRAndDItem,
    }),
    [defaultRows],
  );
};

export const rAndDInitialValues = {
  [R_AND_D_FORM_NAMES.R_AND_D_CURRENCY]: DIMENSIONALITY_VALUES.THOUSAND,
  [R_AND_D_FORM_NAMES.R_AND_D_TABLE]: getDefaultRAndDRows(itemsColumnDefaultValues),
  [R_AND_D_FORM_NAMES.R_AND_D_TABLE_TOTAL]: defaultRAndDItem,
};
